/* ============
 * Bootstrap File
 * ============
 *
 * Will configure and bootstrap the application.
 */

/* ============
 * Vue
 * ============
 *
 * Vue.js is a library for building interactive web interfaces.
 * It provides data-reactive components with a simple and flexible API.
 *
 * http://rc.vuejs.org/guide/
 */
import Vue from 'vue';

Vue.config.debug = process.env.NODE_ENV !== 'production';

/* ============
 * Axios
 * ============
 *
 * Promise based HTTP client for the browser and node.js.
 * Because Vue Resource has been retired, Axios will now been used
 * to perform AJAX-requests.
 *
 * https://github.com/mzabriskie/axios
 */
import Axios from 'axios';
import authService from '@/services/auth';
import { applyAuthHeader } from '@/utils/authToken';

Axios.defaults.baseURL = `${process.env.API_LOCATION}api/`;
Axios.defaults.headers.common.Accept = 'application/json';
Axios.interceptors.response.use(
  response => response,
  (error) => {
    if (error && error.response && error.response.status === 401) {
      authService.logout();
    }

    return Promise.reject(error && error.response ? error.response : error);
  },
);

applyAuthHeader(Axios);

Vue.$http = Axios;
Object.defineProperty(Vue.prototype, '$http', {
  get() {
    return Axios;
  },
});

/* ============
 * Vuex Router Sync
 * ============
 *
 * Effortlessly keep vue-Router and vuex store in sync.
 *
 * https://github.com/vuejs/vuex-router-sync/blob/master/README.md
 */
import VuexRouterSync from 'vuex-router-sync';
import store from './store';

store.dispatch('auth/checkAuthentication');

/* ============
 * Vue Router
 * ============
 *
 * The official Router for Vue.js. It deeply integrates with Vue.js core
 * to make building Single Page Applications with Vue.js a breeze.
 *
 * http://router.vuejs.org/en/index.html
 */
import VueRouter from 'vue-router';
import routes from './routes';

Vue.use(VueRouter);

export const router = new VueRouter({
  mode: 'history',
  routes,
});
router.beforeEach((to, from, next) => {
  if (to.matched.some(m => m.meta.auth) && !store.state.auth.authenticated) {
    /*
     * If the user is not authenticated and visits
     * a page that requires authentication, redirect to the login page
     */
    next({
      name: 'login.index',
    });
  } else if (to.matched.some(m => m.meta.guest) && store.state.auth.authenticated) {
    /*
     * If the user is authenticated and visits
     * an guest page, redirect to the dashboard page
     */
    next({
      name: 'home.index',
    });
  } else {
    next();
  }
});
VuexRouterSync.sync(store, router);

Vue.router = router;

/* ============
 * Vue i18n
 * ============
 *
 * Internationalization plugin of Vue.js.
 *
 * https://kazupon.github.io/vue-i18n/
 */
import VueI18n from 'vue-i18n';
import messages from './locale';
import lang from 'element-ui/lib/locale/lang/th';
import locale from 'element-ui/lib/locale';

locale.use(lang);

Vue.use(VueI18n);

export const i18n = new VueI18n({
  locale: 'th',
  fallbackLocale: 'th',
  messages,
});

/* ============
 * jQuery
 * ============
 *
 * Require jQuery
 *
 * http://jquery.com/
 */
import jQuery from 'jquery';
import moment from 'moment';

window.$ = window.jQuery = jQuery;
window.moment = moment;

/* ============
 * Bootstrap
 * ============
 *
 * Require bootstrap.
 *
 * http://getbootstrap.com/
 */
// require('bootstrap');
// require('bootstrap/less/bootstrap.less');

/* ============
 * Font Awesome
 * ============
 *
 * Require font-awesome.
 *
 * http://http://fontawesome.io/
 */
require('font-awesome/less/font-awesome.less');

/* ============
 * Styling
 * ============
 *
 * Require the application styling.
 * Stylus is used for this boilerplate.
 *
 * If you don't want to use Stylus, that's fine!
 * Replace the stylus directory with the CSS preprocessor you want.
 * Require the entry point here & install the webpack loader.
 *
 * It's that easy...
 *
 * http://stylus-lang.com/
 */
require('./assets/stylus/app.styl');
require('./assets/css/custom.css');

/*
* Element UI
*/

import Element from 'element-ui';
import 'element-ui/lib/theme-default/index.css';

Vue.use(Element);

import Vue2Filters from 'vue2-filters';

Vue.use(Vue2Filters);

Vue.component('clip-loader', require('vue-spinner/src/ClipLoader.vue'));

Vue.use(require('vue-moment'));

export default {
  router,
  i18n,
};



// WEBPACK FOOTER //
// ./src/bootstrap.js